import { MouseEvent, useContext } from 'react'

import { Heading, Icon } from '@hhgtech/hhg-components'
import { Popover } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, BoxProps, Button } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useIntl } from 'react-intl'

import { Disclaimer } from 'components/atoms/disclaimer'
import { useHandlerUrls } from 'hooks/useHandlerUrls'
import { useIsMobile } from 'hooks/useIsMobile'
import { useLeavePageConfirm } from 'hooks/useLeavePageConfirm'
import { HeroBanner } from 'interfaces/types'

import useStyles from './index.styles'
import { HeroBannerContext } from '../context'

export type BannerItemProps = HeroBanner & BoxProps

const SingleBannerItem = ({
  desktopBanner,
  mobileBanner,
  linkTo,
  title,
  ctaTitle,
  styles,
  ratio,
  className,
  classNames,
  dataTracking,
  hasLeavePageConfirm,
  keepRatio = true,
  onBannerLoad,
  ...boxProps
}: BannerItemProps) => {
  const { formatMessage: f } = useIntl()
  const { classes, cx } = useStyles(undefined, {
    name: 'CarePathHeroBannerBannerItem',
    styles,
    classNames,
  })
  const { onItemClick } = useContext(HeroBannerContext)
  const [opened, { close, open }] = useDisclosure(false)
  const isMobile = useIsMobile()
  const linkData = useHandlerUrls([linkTo])

  const bannerEle = keepRatio ? (
    <AspectRatio
      ratio={ratio || (isMobile ? 375 / 500 : 1920 / 600)}
      sx={{ img: { display: 'block' } }}
    >
      <img
        loading="eager"
        src={isMobile ? mobileBanner : desktopBanner}
        alt=""
      />
    </AspectRatio>
  ) : (
    <Box sx={{ img: { display: 'block' } }}>
      <img
        loading="eager"
        src={isMobile ? mobileBanner : desktopBanner}
        alt=""
        onLoad={() => setTimeout(() => onBannerLoad?.(), 400)}
      />
    </Box>
  )

  const { formatUrl, isValidUrl, isExternalUrl, isExternalSite } =
    linkData?.[0] || {}

  const { handleClick } = useLeavePageConfirm(formatUrl)

  const boxWrapper: any = {
    component: isValidUrl ? 'a' : 'div',
    href: formatUrl,
    target: isExternalUrl ? '_blank' : '_self',
    'data-event-category': 'Service Connection',
    'data-event-action': 'Banner Click',
    'data-event-label': formatUrl,
    ...dataTracking,
    onClick: hasLeavePageConfirm
      ? handleClick
      : (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
          if (!isExternalUrl && formatUrl?.includes('#')) {
            e.preventDefault()
            const splitArr = formatUrl.split('#')
            onItemClick?.(splitArr[splitArr.length - 1])
          }
        },
  }

  const btnEle = (
    <Button
      data-primary-btn="true"
      onMouseEnter={open}
      onMouseLeave={close}
      classNames={{
        root: classes.ctaBtn,
        rightIcon: classes.ctaBtnRightIcon,
      }}
      size="lg"
      rightIcon={
        <Icon.ArrowRightTail
          className={classes.ctaArrow}
          color="currentColor"
          size={isMobile ? 20 : 24}
        />
      }
    >
      {ctaTitle}
    </Button>
  )

  return (
    <Box
      className={cx(classes.root, className)}
      sx={() => ({
        overflow: 'hidden',
        img: {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        },
      })}
      {...boxProps}
    >
      {ctaTitle ? (
        <Box className={classes.wrapper}>
          {bannerEle}
          {title && (
            <Heading className={classes.heading} tag="h1">
              {title}
            </Heading>
          )}
          <Box
            {...boxWrapper}
            className={cx(classes.cta, 'pointer-event-child-none')}
          >
            {isExternalSite ? (
              <Popover
                width={264}
                withinPortal
                position="bottom"
                withArrow
                shadow="md"
                opened={opened}
                classNames={{
                  dropdown: classes.popoperDropdown,
                  arrow: classes.popoperArrow,
                }}
              >
                <Popover.Target>{btnEle}</Popover.Target>
                <Popover.Dropdown>
                  <Disclaimer
                    classNames={{
                      root: classes.disclaimer,
                      content: classes.disclaimerText,
                    }}
                    content={f({
                      id: 'confirmLeavePage.content',
                      defaultMessage:
                        'As your trusted health partner, we want to ensure you have the information you need to safeguard your health. Are you sure you want to leave this page?',
                    })}
                  />
                </Popover.Dropdown>
              </Popover>
            ) : (
              btnEle
            )}
          </Box>
        </Box>
      ) : (
        <Box {...boxWrapper} className="pointer-event-child-none">
          <Box className={classes.wrapper}>{bannerEle}</Box>
          {title && (
            <Heading className={classes.heading} tag="h1">
              {title}
            </Heading>
          )}
        </Box>
      )}
    </Box>
  )
}

export { SingleBannerItem }
